import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { BrandLogo } from "@/components/Brand";
import {
    LogOut, Table2, MessageSquareHeart, ShieldAlert, Building2, ChevronDown,
} from "lucide-react";
import NumbersView from "@/components/admin/views/NumbersView";
import FirstCheckinEditor from "@/components/admin/FirstCheckinEditor";
import SafetyFlagsView from "@/components/admin/SafetyFlagsView";
import OrganizationsView from "@/components/admin/views/OrganizationsView";
import ContentStudio from "@/components/admin/ContentStudio";
import CaregiverToolsView from "@/components/admin/views/CaregiverToolsView";
import WeeklyEducationStudio from "@/components/admin/WeeklyEducationStudio";
import ArticleLibrary from "@/components/admin/ArticleLibrary";
import ApplicationsView from "@/components/admin/views/ApplicationsView";
import FeedbackView from "@/components/admin/views/FeedbackView";
import LinksView from "@/components/admin/views/LinksView";
import GuideDemoView from "@/components/admin/views/GuideDemoView";
import CareNetworkView from "@/components/admin/views/CareNetworkView";
import CommunityPartnersView from "@/components/admin/views/CommunityPartnersView";
import PartnerLeadsView from "@/components/admin/views/PartnerLeadsView";
import ReferralSheetsView from "@/components/admin/views/ReferralSheetsView";
import PaymentsView from "@/components/admin/views/PaymentsView";
import UpcomingAppointmentsView from "@/components/admin/views/UpcomingAppointmentsView";
import AccessCodesView from "@/components/admin/views/AccessCodesView";
import SocialStudioView from "@/components/admin/views/SocialStudioView";
import DataQuality from "@/components/admin/os/DataQuality";
import ResearchFeedback from "@/components/admin/os/ResearchFeedback";

/* Four pages run the company. Everything else lives behind More.
 * Consolidated Jul 2026 (founder request): deleted Command Center,
 * Executive, Product analytics, Caregiver journey, Engagement & retention,
 * Enterprise, Monday review, Analytics (legacy), Beta activity, Prolific
 * diagnostics, YC Activity. New metrics go INTO the Numbers table,
 * never onto a new page. */
const MAIN = [
    { v: "numbers", label: "Numbers", Icon: Table2 },
    { v: "first_checkin", label: "Check-in responses", Icon: MessageSquareHeart },
    { v: "safety_flags", label: "Safety flags", Icon: ShieldAlert },
    { v: "orgs", label: "Organizations", Icon: Building2 },
];

const MORE = [
    { v: "content", label: "Content Studio" },
    { v: "caregiver_tools", label: "Caregiver Tools (30 Days)" },
    { v: "social", label: "Social Studio" },
    { v: "library", label: "Article Library" },
    { v: "links", label: "Links" },
    { v: "applications", label: "Applications" },
    { v: "feedback", label: "Feedback inbox" },
    { v: "community", label: "Community partners" },
    { v: "partner_leads", label: "Partner leads" },
    { v: "referrals", label: "GUIDE referral sheets" },
    { v: "access_codes", label: "Partner access codes" },
    { v: "carenet", label: "Care network" },
    { v: "payments", label: "Payments" },
    { v: "appointments", label: "Upcoming sessions" },
    { v: "guide_demo", label: "GUIDE demo" },
    { v: "dataq", label: "Data quality" },
    { v: "research", label: "Research & feedback" },
];

const VALID = new Set([...MAIN.map(m => m.v), ...MORE.map(m => m.v)]);

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [view, setView] = useState(() => {
        if (typeof window === "undefined") return "numbers";
        const p = new URLSearchParams(window.location.search);
        const v = p.get("view") || "numbers";
        // Deleted views (command, exec, analytics, beta, yc…) land on Numbers.
        return VALID.has(v) ? v : "numbers";
    });

    useEffect(() => {
        if (typeof window === "undefined") return;
        const p = new URLSearchParams(window.location.search);
        if ((p.get("view") || "numbers") === view) return;
        p.set("view", view);
        window.history.replaceState({}, "", `${window.location.pathname}?${p.toString()}`);
    }, [view]);

    return (
        <div className="min-h-screen bg-cream">
            <header className="bg-cream/80 backdrop-blur-sm border-b border-[#E2E8F0]/60 sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/admin" className="flex items-center gap-3">
                        <BrandLogo className="h-6" />
                        <div className="leading-tight border-l border-[#D8DBD9] pl-3">
                            <div className="font-heading font-semibold text-sm">Admin</div>
                            <div className="text-xs text-[#4C5551] -mt-0.5">Signed in as {user?.email}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/dashboard" data-testid="admin-to-app" className="text-sm font-medium text-[#4C5551] hover:text-teal-dark px-3 py-2">Open app</Link>
                        <button onClick={logout} data-testid="admin-logout" className="text-sm font-medium text-[#4C5551] hover:text-teal-dark inline-flex items-center gap-1 px-3 py-2">
                            <LogOut size={16} strokeWidth={1.6} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
                <div className="flex flex-wrap items-center gap-2 mb-7" data-testid="admin-view-switcher">
                    {MAIN.map(({ v, label, Icon }) => (
                        <button key={v} onClick={() => setView(v)} data-testid={`admin-view-${v}`}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${view === v
                                ? "bg-teal text-white border-teal"
                                : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"}`}>
                            <Icon size={15} strokeWidth={1.8} /> {label}
                        </button>
                    ))}
                    <MoreMenu view={view} setView={setView} />
                </div>

                {view === "numbers" && <NumbersView />}
                {view === "first_checkin" && <FirstCheckinEditor />}
                {view === "safety_flags" && <SafetyFlagsView />}
                {view === "orgs" && <OrganizationsView />}
                {view === "content" && (<><ContentStudio /><WeeklyEducationStudio /></>)}
                {view === "caregiver_tools" && <CaregiverToolsView />}
                {view === "social" && <SocialStudioView />}
                {view === "library" && <ArticleLibrary />}
                {view === "links" && <LinksView />}
                {view === "applications" && <ApplicationsView />}
                {view === "feedback" && <FeedbackView />}
                {view === "community" && <CommunityPartnersView />}
                {view === "partner_leads" && <PartnerLeadsView />}
                {view === "referrals" && <ReferralSheetsView />}
                {view === "access_codes" && <AccessCodesView />}
                {view === "carenet" && <CareNetworkView />}
                {view === "payments" && <PaymentsView />}
                {view === "appointments" && <UpcomingAppointmentsView />}
                {view === "guide_demo" && <GuideDemoView />}
                {view === "dataq" && <DataQuality />}
                {view === "research" && <ResearchFeedback />}
            </main>
        </div>
    );
}

function MoreMenu({ view, setView }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    const activeInside = MORE.some(m => m.v === view);
    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
                data-testid="admin-more"
                className={`inline-flex items-center gap-1 px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${activeInside
                    ? "bg-teal text-white border-teal"
                    : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"}`}>
                More{activeInside ? `: ${MORE.find(m => m.v === view)?.label}` : ""} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div role="menu" data-testid="admin-more-menu"
                    className="absolute left-0 top-full mt-1.5 z-40 w-64 rounded-2xl border border-cream-dark bg-white shadow-lg p-1.5 max-h-[70vh] overflow-y-auto">
                    {MORE.map(({ v, label }) => (
                        <button key={v} role="menuitem" data-testid={`admin-view-${v}`}
                            onClick={() => { setView(v); setOpen(false); }}
                            className={`w-full text-left rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${view === v ? "bg-teal text-white" : "text-[#4C5551] hover:bg-cream-light"}`}>
                            {label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
